import React from 'react';
import { useTranslation } from 'react-i18next';
import { Paperclip } from 'lucide-react';

const MAX_BYTES = 25 * 1024 * 1024; // Limite SMTP classique (OVH) : 25 Mo

// Récupère la taille en octets quelle que soit la forme de la PJ
// (File brut, {file}, {size}, {file_size}, PJ CRM)
const getSize = (a) => {
  if (!a) return 0;
  if (a instanceof File) return a.size;
  return a.file?.size ?? a.size ?? a.file_size ?? 0;
};

/**
 * Barre de progression de la taille cumulée des pièces jointes (ex : « 8,3 / 25 Mo »).
 * Passe au rouge à l'approche / au dépassement de la limite.
 */
export const AttachmentSizeBar = ({ attachments = [], maxBytes = MAX_BYTES }) => {
  const { t } = useTranslation('admin');
  if (!attachments || attachments.length === 0) return null;

  const total = attachments.reduce((s, a) => s + getSize(a), 0);
  const mb = total / (1024 * 1024);
  const maxMb = maxBytes / (1024 * 1024);
  const pct = Math.min(100, (total / maxBytes) * 100);
  const over = total > maxBytes;
  const warn = !over && pct >= 80;

  const barColor = over ? 'bg-red-500' : warn ? 'bg-amber-500' : 'bg-emerald-500';
  const textColor = over ? 'text-red-600' : warn ? 'text-amber-600' : 'text-slate-500';

  return (
    <div className="mt-3" data-testid="attachment-size-bar">
      <div className="flex items-center justify-between mb-1">
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <Paperclip className="w-3.5 h-3.5" />
          {t('mail.attachSizeCount', { count: attachments.length })}
        </span>
        <span data-testid="attachment-size-label" className={`text-xs font-semibold ${textColor}`}>
          {mb.toFixed(1)} / {maxMb.toFixed(0)} {t('mail.attachSizeUnit')}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${pct}%` }}
          data-testid="attachment-size-fill"
        />
      </div>
      {over && (
        <p className="mt-1 text-xs text-red-600" data-testid="attachment-size-error">
          {t('mail.attachSizeOver', { max: maxMb.toFixed(0) })}
        </p>
      )}
    </div>
  );
};

export default AttachmentSizeBar;
